import React from 'react';
import { emptyState } from '@techchange/pm-specs';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import {
	Button,
	FormGroup,
	Label,
	Modal,
	ModalBody,
	ModalHeader,
	ModalFooter
} from 'reactstrap';

import { messages } from '../../../../../../_shared/i18n/messages';

import { FIELD_TYPES } from '../../../../../constants';
import FormField from '../../../../FormField';
import TextEditor from '../../../../TextEditor';
import { useFormEditorContext } from '../../../contexts/FormEditorContext';
import {
	FIELD_EDITOR_FIELDS,
	OPTION_EDITOR_FIELDS,
} from '../../../hooks/useFormEditorData';
import { formFieldDataShape } from '../../../utils/propTypes';

import './FormEditorFieldAnswersAndFeedbackModal.scss';

const propTypes = {

	/**
	 * Flag to control if the modal is open.
	 */
	isOpen: PropTypes.bool,

	/**
	 * The form field.
	 */
	field: formFieldDataShape.isRequired,

	/**
	 * Callback function to change the form field.
	 */
	onChange: PropTypes.func.isRequired,

	/**
	 * Callback function to toggle the modal.
	 */
	onToggle: PropTypes.func.isRequired,

};

const FormEditorFieldAnswersAndFeedbackModal = (props) => {

	const {
		isOpen,
		field,
		onChange,
		onToggle,
	} = props;

	const intl = useIntl();

	const {
		selectedLanguage,
		mobileView,
		filestack,
	} = useFormEditorContext();

	const fieldType = field[FIELD_EDITOR_FIELDS.TYPE];

	// Get the form field content data for the selected language
	const activeTranslation =
		field[FIELD_EDITOR_FIELDS.CONTENTS].find(
			(content) => content.language === selectedLanguage
		);

	// Check if the question supports choosing the correct vs. incorrect answers
	const hasCorrectAndIncorrectAnswers =
		fieldType === FIELD_TYPES.CHECKBOXES ||
		fieldType === FIELD_TYPES.RADIO;

	const dummyInputOptions = field[FIELD_EDITOR_FIELDS.OPTIONS]?.map((option, index) => {

		// Get the form field option content data for the selected language
		const activeOptionTranslation =
			option[OPTION_EDITOR_FIELDS.CONTENTS].find(
				(content) => content.language === selectedLanguage
			);

		const component = (
			<div className="d-flex">
				<span>
					{
						activeOptionTranslation?.[OPTION_EDITOR_FIELDS.LABEL] ||
						intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_MSG_TEXT_FIELD_OPTION_NAME, {
							name: index + 1
						})
					}
				</span>
				{option[OPTION_EDITOR_FIELDS.IS_CORRECT_ANSWER] &&
					<span className="ms-auto">
						<i
							aria-hidden="true"
							className="fas fa-check" />
					</span>
				}
			</div>
		);

		return {
			id: option.id,
			label: component,
			value: option.id,
		};

	});

	const handleDummyInputChange = (name, value) => {

		const updatedOptions = field[FIELD_EDITOR_FIELDS.OPTIONS].map((option) => {

			let isSelected = false;

			if (fieldType === FIELD_TYPES.CHECKBOXES) {

				isSelected = value.indexOf(option.id) >= 0;

			} else if (fieldType === FIELD_TYPES.RADIO) {

				isSelected = value === option.id;

			}

			return {
				...option,
				[OPTION_EDITOR_FIELDS.IS_CORRECT_ANSWER]: isSelected,
			};

		});

		onChange(field.id, FIELD_EDITOR_FIELDS.OPTIONS, updatedOptions);

	};

	// Choose the the "dummy" input to present as a preview of the option type
	let dummyInputValue;

	if (fieldType === FIELD_TYPES.CHECKBOXES) {

		dummyInputValue = field[FIELD_EDITOR_FIELDS.OPTIONS]
			.filter((option) => option[OPTION_EDITOR_FIELDS.IS_CORRECT_ANSWER])
			.map((option) => option.id);

	} else if (fieldType === FIELD_TYPES.RADIO) {

		dummyInputValue = field[FIELD_EDITOR_FIELDS.OPTIONS]
			.find((option) => option[OPTION_EDITOR_FIELDS.IS_CORRECT_ANSWER])?.id || null;

	}

	return (
		<Modal
			className="tc-form-editor-field-answer-and-feedback-modal"
			isOpen={isOpen}
			size="lg"
			toggle={onToggle}>
			{hasCorrectAndIncorrectAnswers &&
				<>
					<ModalHeader tag="h2">
						<FormattedMessage {...messages.TC_COMPONENTS_FORM_EDITOR_FIELD_CHOOSE_ANSWER_AND_FEEDBACK_MODAL_ANSWER_HEADER} />
					</ModalHeader>
					<ModalBody>
						<FormField
							type={field.type}
							name={`choose-correct-answers-for-field-${field.id}`}
							label={activeTranslation?.[FIELD_EDITOR_FIELDS.TITLE] || ''}
							value={dummyInputValue}
							options={dummyInputOptions}
							isEditing
							onChange={handleDummyInputChange} />
					</ModalBody>
				</>
			}
			<ModalHeader tag="h2">
				<FormattedMessage {...messages.TC_COMPONENTS_FORM_EDITOR_FIELD_CHOOSE_ANSWER_AND_FEEDBACK_MODAL_FEEDBACK_HEADER} />
			</ModalHeader>
			<ModalBody>
				<FormGroup>
					<Label for="feedback-correct">
						<FormattedMessage {...messages.TC_COMPONENTS_FORM_EDITOR_FIELD_LABEL_TEXT_SUBMISSION_FEEDBACK_CORRECT} />
					</Label>
					<TextEditor
						id="feedback-correct"
						editorDomId={`editor-field-${field.id}-submission-feedback-correct`}
						className="tc-form-editor-field-submission-feedback-container"
						innerClassName="tc-form-editor-field-submission-feedback-wysiwyg"
						content={
							activeTranslation?.[FIELD_EDITOR_FIELDS.SUBMISSION_FEEDBACK_CORRECT] || emptyState
						}
						placeholderText={intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_FIELD_LABEL_TEXT_SUBMISSION_FEEDBACK_CORRECT)}
						mobileView={mobileView}
						filestackSettings={filestack}
						modalView
						onChange={(newValue) => onChange(
							field.id,
							FIELD_EDITOR_FIELDS.SUBMISSION_FEEDBACK_CORRECT,
							newValue,
						)} />
				</FormGroup>
				{hasCorrectAndIncorrectAnswers &&
					<FormGroup>
						<Label for="feedback-incorrect">
							<FormattedMessage {...messages.TC_COMPONENTS_FORM_EDITOR_FIELD_LABEL_TEXT_SUBMISSION_FEEDBACK_INCORRECT} />
						</Label>
						<TextEditor
							id="feedback-incorrect"
							editorDomId={`editor-field-${field.id}-submission-feedback-incorrect`}
							className="tc-form-editor-field-submission-feedback-container"
							innerClassName="tc-form-editor-field-submission-feedback-wysiwyg"
							content={
								activeTranslation?.[FIELD_EDITOR_FIELDS.SUBMISSION_FEEDBACK_INCORRECT] || emptyState
							}
							placeholderText={intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_FIELD_LABEL_TEXT_SUBMISSION_FEEDBACK_INCORRECT)}
							mobileView={mobileView}
							filestackSettings={filestack}
							modalView
							onChange={(newValue) => onChange(
								field.id,
								FIELD_EDITOR_FIELDS.SUBMISSION_FEEDBACK_INCORRECT,
								newValue,
							)} />
					</FormGroup>
				}
			</ModalBody>
			<ModalFooter>
				<Button
					color="primary"
					type="button"
					onClick={onToggle}>
					<FormattedMessage {...messages.TC_COMPONENTS_GENERAL_DONE_LABEL} />
				</Button>
			</ModalFooter>
		</Modal>
	);

};

FormEditorFieldAnswersAndFeedbackModal.propTypes = propTypes;

export default FormEditorFieldAnswersAndFeedbackModal;
